"use client";

import React, { useMemo, useState } from "react";
import type { AccountAnalytics } from "@/vybe-trader-client/trade-history/accountAnalytics";
import {
  AnalyticsCard,
  AnalyticsCardHeader,
  AnalyticsTab,
  SplineColumnChart,
  fmtDayMonth,
  fmtMoney,
  fmtSignedMoney,
  fmtSignedPct,
  pnlClass,
} from "./analyticsShared";

type ChartMode = "growth" | "balance" | "profit";

/**
 * Chart card — Growth / Balance / Profit spline with a column overlay:
 * lots-per-day under Growth/Balance, daily P&L (blue/red) under Profit.
 */
export default function AnalyticsChartCard({
  analytics,
  currency,
}: {
  analytics: AccountAnalytics;
  currency: string;
}) {
  const [mode, setMode] = useState<ChartMode>("growth");

  const growthAvailable = analytics.stats.gainPct !== null;
  const effectiveMode: ChartMode =
    mode === "growth" && !growthAvailable ? "profit" : mode;

  const line = useMemo(() => {
    return analytics.equity
      .filter((p) => p.t > 0)
      .map((p) => ({
        t: p.t,
        v:
          effectiveMode === "balance"
            ? p.balance
            : effectiveMode === "profit"
              ? p.profit
              : (p.growthPct ?? 0),
      }));
  }, [analytics.equity, effectiveMode]);

  const columns = useMemo(
    () =>
      analytics.dailyBuckets.map((b) => ({
        t: b.dayStart,
        v: effectiveMode === "profit" ? b.pnl : b.lots,
      })),
    [analytics.dailyBuckets, effectiveMode]
  );

  const headline =
    effectiveMode === "growth"
      ? fmtSignedPct(analytics.stats.gainPct)
      : effectiveMode === "balance"
        ? fmtMoney(analytics.stats.endBalance, currency)
        : fmtSignedMoney(analytics.stats.netProfit, currency);

  const headlineClass =
    effectiveMode === "balance"
      ? "text-foreground"
      : pnlClass(analytics.stats.netProfit);

  return (
    <AnalyticsCard className="flex flex-col">
      <AnalyticsCardHeader>
        <span className="mr-1">Chart</span>
        <AnalyticsTab
          active={effectiveMode === "growth"}
          onClick={() => setMode("growth")}
        >
          Growth
        </AnalyticsTab>
        <AnalyticsTab
          active={effectiveMode === "balance"}
          onClick={() => setMode("balance")}
        >
          Balance
        </AnalyticsTab>
        <AnalyticsTab
          active={effectiveMode === "profit"}
          onClick={() => setMode("profit")}
        >
          Profit
        </AnalyticsTab>
        <span
          className={`ml-auto font-mono tabular-nums normal-case text-[11px] ${headlineClass}`}
        >
          {headline}
        </span>
      </AnalyticsCardHeader>

      <div className="p-2 pb-1 flex-1">
        <SplineColumnChart
          line={line}
          columns={columns}
          signedColumns={effectiveMode === "profit"}
        />
        <div className="flex items-center justify-between pt-1 font-mono text-[9px] text-muted-foreground">
          <span>
            {fmtDayMonth(analytics.firstTradeMs)} →{" "}
            {fmtDayMonth(analytics.lastTradeMs)}
          </span>
          <span>
            {effectiveMode === "profit"
              ? "line · cumulative profit — columns · daily P&L"
              : effectiveMode === "balance"
                ? "line · balance — columns · lots/day"
                : "line · growth % — columns · lots/day"}
          </span>
        </div>
      </div>
    </AnalyticsCard>
  );
}
